<template>
  <b-card no-body class="loan-form loan-actions loan-actions-takeover">
    <b-card-header
      id="takeover"
      header-tag="header"
      role="tab"
      class="loan-actions__header"
      v-b-toggle.loan-actions-takeover
    >
      <b-icon icon="chevron-down" class="toggle-icon"></b-icon>
      <h2>
        <svg-danger v-if="(action.status === 'in_process' && loanIsCanceled) || action.status === 'canceled'" />
        <svg-waiting v-else-if="action.status === 'in_process'" />
        <svg-check v-else-if="action.status === 'completed'" />
        Au départ
      </h2>

      <span v-if="item.is_contested && action.status === 'in_process' && !loanIsCanceled">
        Bloqué
      </span>
      <span v-else>
        <!--
          Canceled loans: current step remains in-process.
          Canceled action means contestation. Give way to canceled-loan status.
        -->
        <span
          v-if="(action.status === 'in_process' || action.status === 'canceled') && loanIsCanceled"
        >
          Emprunt annulé &bull; {{ item.canceled_at | datetime }}
        </span>
        <span v-else-if="action.status == 'in_process'"> En attente </span>
        <span v-else-if="action.status === 'completed'">
          Complété &bull; {{ action.executed_at | datetime }}
        </span>
        <span v-else-if="action.status === 'canceled'">
          Contesté &bull; {{ action.executed_at | datetime }}
        </span>
      </span>
    </b-card-header>

    <b-card-body>
      <b-collapse
        id="loan-actions-takeover"
        role="tabpanel"
        accordion="loan-actions"
        :visible="open"
      >
        <div
          v-if="(action.status === 'in_process' || action.status === 'canceled') && loanIsCanceled"
        >
          <p>L'emprunt a été annulé. Cette étape ne peut pas être complétée.</p>
        </div>

        <div v-else>
          <b-alert show variant="warning" v-if="item.loanable.instructions">
            <div class="alert-heading">
              <h4>Instructions du propriétaire pour l'utilisation du véhicule</h4>
            </div>
            <div class="owner-instructions-text">
              <p>{{ item.loanable.instructions }}</p>
            </div>
          </b-alert>

          <div v-if="item.loanable.type === 'car'">
            <validation-observer ref="observer" v-slot="{ passes }">
              <b-row>
                <b-col>
                  <p>
                    Avant votre départ, merci de penser à noter le kilométrage qui figure sur le compteur de la voiture.
                  </p>
                </b-col>
              </b-row>

              <b-form
                :novalidate="true"
                class="loan-actions-takeover__form"
                @submit.stop.prevent="passes(completeAction)"
              >
                <b-row>
                  <b-col lg="6">
                    <forms-validated-input
                      id="mileage_beginning"
                      name="mileage_beginning"
                      type="number"
                      :rules="{ required: true, min_value: 1 }"
                      label="KM au compteur, au début de la course"
                      :placeholder="mileageWasFilledAutomatically ? 'donnée non disponible' : 'KM au compteur'"
                      :disabled="((lockMileage && !!action.executed_at) || loanIsFinished || loanIsCanceled || !loanDayIsTodayOrBefore || !userRoles.includes('borrower')) && !userIsAdmin"
                      v-model="mileageWasFilledAutomatically ? '' : action.mileage_beginning"
                    />
                  </b-col>
                </b-row>

                <b-row
                  class="loan-actions-takeover__buttons text-center"
                  v-if="
                    (!action.executed_at && !loanIsCanceled && !item.is_contested) || userIsAdmin
                  "
                >
                  <b-col>
                    <b-button type="submit" size="sm" variant="success" class="mr-3" :disabled="!userIsAdmin && !userRoles.includes('borrower')">
                      <span v-if="isContested">Corriger</span>
                      <span v-else>Enregistrer</span>
                    </b-button>
                  </b-col>
                </b-row>

                <b-row class="loan-actions__alert" v-if="!action.executed_at && !loanIsCanceled && !loanDayIsTodayOrBefore">
                  <b-col>
                    <b-alert variant="warning" show>
                      Il sera possible de compléter le kilométrage de départ le jour J !
                    </b-alert>
                  </b-col>
                </b-row>
              </b-form>
            </validation-observer>
          </div>

          <div v-else-if="item.loanable.has_padlock">
            <!-- Loanable is not a car and has a padlock. -->

            <b-alert show variant="info">
              <p>
                Le cadenas du véhicule sera automatiquement associé à votre application NOKE à temps
                pour la prise de possession.
              </p>
              <p>Vous avez un problème avec le cadenas?</p>
              <p>
                Contactez-nous entre 9h et 20h au 438-476-3343<br />
                (cette ligne est dédiée uniquement aux problèmes liés aux cadenas)
              </p>
            </b-alert>

            <validation-observer ref="observer" v-slot="{ passes }">
              <b-form
                :novalidate="true"
                class="register-form__form"
                @submit.stop.prevent="passes(completeAction)"
              >
                <b-row v-if="!action.executed_at">
                  <b-col>
                    <p>Envoyez une photo de l'état du véhicule.</p>

                    <forms-image-uploader
                      label="Photo du véhicule"
                      field="image"
                      v-model="action.image"
                    />

                    <p>
                      <small>
                        Cette photo est optionnelle mais permet à Coloc'Auto de déterminer à quel
                        moment un bris s'est produit, le cas échéant.
                      </small>
                    </p>
                  </b-col>
                </b-row>
                <b-row v-else-if="action.image">
                  <b-col>
                    <a href="#" v-b-modal="'takeover-image'">
                      <img :src="action.image ? action.image.sizes.thumbnail : ''" />
                    </a>

                    <b-modal
                      size="xl"
                      title="Photo de l'état du véhicule"
                      :id="'takeover-image'"
                      footer-class="d-none"
                    >
                      <img class="img-fit" :src="action.image.url" />
                    </b-modal>
                  </b-col>
                </b-row>

                <b-row
                  class="loan-actions-takeover__buttons text-center"
                  v-if="(!action.executed_at && !item.is_contested) || userIsAdmin"
                >
                  <b-col>
                    <b-button type="submit" size="sm" variant="success" class="mr-3">
                      <span v-if="isContested">Corriger</span>
                      <span v-else>Enregistrer</span>
                    </b-button>
                  </b-col>
                </b-row>

                <b-row class="loan-actions__alert" v-if="!action.executed_at">
                  <b-col>
                    <b-alert variant="warning" show>
                      Les informations de l'emprunt peuvent être modifiées jusqu'à 48h après sa
                      conclusion. À partir de ce moment, le coût de l'emprunt sera validé avec les
                      détails ci-dessus.
                    </b-alert>
                  </b-col>
                </b-row>
              </b-form>
            </validation-observer>
          </div>

          <div v-else>
            <!-- Loanable is not a car and it does not have a padlock. -->
            <b-row v-if="!action.executed_at">
              <b-col>
                <p v-if="userRoles.includes('borrower') && !userRoles.includes('owner')">
                  Demandez au propriétaire de récupérer le véhicule.
                </p>
                <p v-if="!userRoles.includes('borrower') && userRoles.includes('owner')">
                  L'emprunteur vous contactera pour arranger la prise de possession du véhicule.
                </p>
              </b-col>
            </b-row>
            <b-row v-else>
              <b-col>
                <p v-if="action.status !== 'canceled'">La prise de possession a été effectuée.</p>
                <p v-else>La prise de possession a été annulée.</p>
              </b-col>
            </b-row>

            <b-row class="loan-actions-takeover__buttons text-center" v-if="!action.executed_at">
              <b-col>
                <b-button
                  type="submit"
                  size="sm"
                  variant="success"
                  class="mr-3"
                  :disabled="actionLoading"
                  @click="completeAction"
                >
                  C'est fait!
                </b-button>
              </b-col>
            </b-row>

            <b-row class="loan-actions__alert" v-if="!action.executed_at">
              <b-col>
                <b-alert variant="warning" show>
                  Les informations de l'emprunt peuvent être modifiées jusqu'à 48h après sa
                  conclusion. À partir de ce moment, le coût de l'emprunt sera validé avec les
                  détails ci-dessus.
                </b-alert>
              </b-col>
            </b-row>
          </div>
        </div>

        <div v-if="!isContested">
          <div v-if="!loanIsFinished && isContestable && !loanIsCanceled">
            <hr />

            <validation-observer ref="observer" v-slot="{ passes }">
              <b-form
                :novalidate="true"
                class="loan-actions-takeover__contestation"
                @submit.stop.prevent="passes(cancelAction)"
              >
                <b-row>
                  <b-col lg="6">
                    <template v-if="lockMileage">
                      <p>Cette information est-elle incorrecte?</p>
                      <b-button  @click="lockMileage = false" variant="primary">
                        Modifier
                      </b-button>
                    </template>
                    <b-button v-else @click="update" variant="primary">
                      Enregistrer
                    </b-button>
                  </b-col>
                </b-row>
              </b-form>
            </validation-observer>
          </div>
        </div>
        <div v-else-if="!loanIsCanceled">
          <hr />

          <p>Les données ont été contestées {{ action.comments_on_contestation ? `:` : `` }}</p>

          <b-alert variant="warning" v-if="action.comments_on_contestation" show>
            {{ action.comments_on_contestation }}
          </b-alert>

          <p>
            Un membre de l'équipe Coloc'Auto contactera les participant-e-s et ajustera les données.
          </p>
        </div>
      </b-collapse>
    </b-card-body>
  </b-card>
</template>

<script>
import FormsImageUploader from "@/components/Forms/ImageUploader.vue";
import FormsValidatedInput from "@/components/Forms/ValidatedInput.vue";

import LoanActionsMixin from "@/mixins/LoanActionsMixin";

export default {
  name: "LoanActionsTakeover",
  mixins: [LoanActionsMixin],
  components: {
    FormsImageUploader,
    FormsValidatedInput,
  },
  data(){
    return {
      lockMileage: true,
    }
  },
  methods: {
    update() {
      this.updateMileage();
      this.lockMileage = true;
    }
  },
};
</script>

<style lang="scss">
.loan-actions-takeover {
  &__form {
    &__image a {
      display: inline-block;
      margin-bottom: 1rem;
    }
  }
}
.owner-instructions-text {
  white-space: pre-wrap;
}
</style>
